// Gates an action behind "project is saved". If the project has an id we just
// re-save the current state; if it has only a name we save under that name; if
// neither, we open a name dialog (same UX as the toolbar Save button).
//
// Used in front of the Export dialog so the rendered scene matches a persisted
// project — and so the render-history "Open project" link points somewhere real.

import {useCallback, useState, type ReactNode} from 'react'
import {useParams} from 'react-router-dom'
import {Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from '../../common/store'
import {useEditorConfig} from '../../common/hooks/useEditorConfig'
import {getProjectId} from '../../common/components/store/projectIdStore'
import {getProjectName} from '../../common/components/store/projectNameStore'
import {useToast} from '../../common/components/toast/toastContext'
import {handleApiError} from '../../common/errors/handleApiError'
import {saveProject} from '../components/topMenuBar/saveProjectFn'

interface UseEnsureSavedProjectResult {
  /**
   * Save the current project state. Returns true when persisted (existing or
   * freshly created), false when the user cancelled the name dialog or save
   * failed.
   */
  ensureSaved: () => Promise<boolean>
  /** Name dialog markup — render alongside the trigger. */
  dialog: ReactNode
  /** Whether a save is in flight (drives spinner / disabled state on triggers). */
  isSaving: boolean
}

const useEnsureSavedProject = (): UseEnsureSavedProjectResult => {
  const {t} = useTranslation()
  const toast = useToast()
  const dispatch = useAppDispatch()
  const {deviceConfig} = useEditorConfig()
  const {projectId: routeProjectId, projectSlug} = useParams()

  const [nameDialogOpen, setNameDialogOpen] = useState(false)
  const [nameInput, setNameInput] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [resolver, setResolver] = useState<((ok: boolean) => void) | null>(null)

  const runSave = useCallback(
    async (existingId: string | undefined, name?: string): Promise<boolean> => {
      setIsSaving(true)
      try {
        await saveProject({
          dispatch,
          deviceConfig,
          existingId,
          name,
          forkedFromSlug: projectSlug,
        })
        return true
      } catch (err) {
        toast.error(handleApiError(err, 'ensureSavedProject') ?? t('toolbar.saveFailed'))
        return false
      } finally {
        setIsSaving(false)
      }
    },
    [dispatch, deviceConfig, projectSlug, t, toast],
  )

  const ensureSaved = useCallback((): Promise<boolean> => {
    const existingId = getProjectId() ?? routeProjectId
    if (existingId && existingId !== 'new') return runSave(existingId)

    const liveName = getProjectName()
    if (liveName) return runSave(undefined, liveName)

    // No id, no name → ask for one. Resolver fires from confirm/cancel handlers.
    setNameInput('')
    setNameDialogOpen(true)
    return new Promise<boolean>((resolve) => {
      setResolver(() => resolve)
    })
  }, [routeProjectId, runSave])

  const handleConfirm = useCallback(async () => {
    const name = nameInput.trim()
    if (!name) return
    setNameDialogOpen(false)
    const ok = await runSave(undefined, name)
    resolver?.(ok)
    setResolver(null)
  }, [nameInput, runSave, resolver])

  const handleCancel = useCallback(() => {
    setNameDialogOpen(false)
    resolver?.(false)
    setResolver(null)
  }, [resolver])

  const dialog = (
    <Dialog open={nameDialogOpen} onClose={handleCancel}>
      <DialogTitle>{t('toolbar.saveProject')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder={t('toolbar.projectNamePlaceholder')}
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && nameInput.trim()) handleConfirm()
          }}
          sx={{mt: 1}}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>{t('toolbar.cancel')}</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!nameInput.trim() || isSaving}>
          {t('toolbar.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )

  return {ensureSaved, dialog, isSaving}
}

export default useEnsureSavedProject
